import { useTranslation } from "react-i18next";
import { DetailRow, SelectRow, TimeRow } from "../Rows";
import { FormCard, FormGrid } from "../FormCard";
import { formatSwedishTime } from "../../helpers/DateTime";

const REBOOT_WEEKDAY_OPTIONS = [
    { value: 1, key: "MONDAY" },
    { value: 2, key: "TUESDAY" },
    { value: 3, key: "WEDNESDAY" },
    { value: 4, key: "THURSDAY" },
    { value: 5, key: "FRIDAY" },
    { value: 6, key: "SATURDAY" },
    { value: 0, key: "SUNDAY" },
];

const KNOWN_STATUSES = ["Skipped", "Requested", "Accepted", "Rejected", "Failed"];
const KNOWN_REASONS = ["ConnectorBusy", "Offline", "Disabled", "DstGap", "Timeout", "PointNotFound", "Unknown"];

export function normalizeRebootTime(value) {
    if (value == null || String(value).trim() === "") {
        return null;
    }

    const match = String(value).trim().match(/^(\d{2}):(\d{2})/);
    if (!match) {
        return null;
    }

    const hours = Number(match[1]);
    const minutes = Number(match[2]);
    if (hours > 23 || minutes > 59 || minutes % 15 !== 0) {
        return null;
    }

    return `${match[1]}:${match[2]}`;
}

function snapToQuarterHour(value) {
    if (value == null || String(value).trim() === "") {
        return "";
    }

    const match = String(value).trim().match(/^(\d{2}):(\d{2})/);
    if (!match) {
        return null;
    }

    let hours = Number(match[1]);
    let minutes = Number(match[2]);
    if (hours > 23 || minutes > 59) {
        return null;
    }

    const snappedMinutes = Math.round(minutes / 15) * 15;
    if (snappedMinutes === 60) {
        hours = (hours + 1) % 24;
        minutes = 0;
    } else {
        minutes = snappedMinutes;
    }

    return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`;
}

// Next moment the schedule is due, as Swedish wall-clock date and time. The reboot itself lands
// within 15 minutes after this, when the evaluator timer picks the slot up.
export function getNextRebootSlot(rebootWeekdays, rebootTimeLocal, now = new Date()) {
    if (!Array.isArray(rebootWeekdays) || rebootWeekdays.length === 0 || !rebootTimeLocal) {
        return null;
    }

    const parts = Object.fromEntries(
        new Intl.DateTimeFormat("en-CA", {
            timeZone: "Europe/Stockholm",
            year: "numeric",
            month: "2-digit",
            day: "2-digit",
            hour: "2-digit",
            minute: "2-digit",
            hourCycle: "h23",
        })
            .formatToParts(now)
            .map((part) => [part.type, part.value])
    );
    const nowTime = `${parts.hour}:${parts.minute}`;

    for (let offset = 0; offset <= 7; offset++) {
        const date = new Date(Date.UTC(Number(parts.year), Number(parts.month) - 1, Number(parts.day) + offset));
        const weekday = date.getUTCDay();

        if (!rebootWeekdays.includes(weekday)) {
            continue;
        }

        if (offset === 0 && rebootTimeLocal <= nowTime) {
            continue;
        }

        return {
            weekday,
            date: date.toISOString().slice(0, 10),
            time: rebootTimeLocal,
        };
    }

    return null;
}

export function buildRebootSchedulePayload(chargingPoint) {
    if (!Array.isArray(chargingPoint?.rebootWeekdays)) {
        return null;
    }

    const rebootWeekdays = [...new Set(
        chargingPoint.rebootWeekdays
            .map((weekday) => Number(weekday))
            .filter((weekday) => Number.isInteger(weekday) && weekday >= 0 && weekday <= 6)
    )];
    const rebootTimeLocal = normalizeRebootTime(chargingPoint.rebootTimeLocal);

    return {
        rebootWeekdays,
        rebootTimeLocal: rebootWeekdays.length > 0 ? rebootTimeLocal : null,
        timeMissing: rebootWeekdays.length > 0 && !rebootTimeLocal,
    };
}

const ScheduledRebootForm = ({
    chargingPoint = {},
    handleInputChange,
    invalidFields,
}) => {
    const { t } = useTranslation();
    const lastReboot = chargingPoint.lastScheduledReboot;
    const schedule = buildRebootSchedulePayload(chargingPoint);
    const nextReboot = getNextRebootSlot(schedule?.rebootWeekdays, schedule?.rebootTimeLocal);
    const nextRebootWeekday = REBOOT_WEEKDAY_OPTIONS.find((day) => day.value === nextReboot?.weekday);
    const weekdayOptions = REBOOT_WEEKDAY_OPTIONS.map((day) => ({
        value: day.value,
        label: t(`weekday.${day.key}`),
    }));

    const statusLabel = (status) => {
        if (!status) {
            return "";
        }
        if (KNOWN_STATUSES.includes(status)) {
            return t(`point.scheduledReboot.status.${status}`);
        }
        return status;
    };

    const reasonLabel = (reason) => {
        if (!reason) {
            return "";
        }
        if (KNOWN_REASONS.includes(reason)) {
            return t(`point.scheduledReboot.reason.${reason}`);
        }
        return reason;
    };

    const statusAndReason = (reboot) => {
        const status = statusLabel(reboot?.status);
        const reason = reasonLabel(reboot?.reason);
        if (status && reason) {
            return t("point.scheduledReboot.statusWithReason", { status, reason });
        }
        return status || reason;
    };

    const formatSlot = (value) => {
        if (!value) {
            return "";
        }
        const date = new Date(value);
        if (Number.isNaN(date.getTime())) {
            return "";
        }
        return formatSwedishTime(value, false);
    };

    return (
        <FormCard
            title={t("point.scheduledReboot.title")}
            description={t("point.scheduledReboot.description")}
            tooltip={t("point.scheduledReboot.retryTooltip")}
        >
            <FormGrid>
                <SelectRow
                    label={t("point.scheduledReboot.weekdays")}
                    tooltip={t("point.scheduledReboot.weekdaysTooltip")}
                    value={Array.isArray(chargingPoint.rebootWeekdays) ? chargingPoint.rebootWeekdays : []}
                    options={weekdayOptions}
                    isMulti
                    isClearable
                    onChange={(values) => handleInputChange("rebootWeekdays", values || [])}
                />
                <TimeRow
                    label={t("point.scheduledReboot.time")}
                    tooltip={t("point.scheduledReboot.timeTooltip")}
                    value={chargingPoint.rebootTimeLocal || ""}
                    invalid={invalidFields?.includes("rebootTimeLocal")}
                    step={900}
                    onChange={(value) => {
                        const snapped = snapToQuarterHour(value);
                        if (snapped == null) {
                            return;
                        }
                        handleInputChange("rebootTimeLocal", snapped);
                    }}
                />
                <DetailRow
                    label={t("point.scheduledReboot.nextSlot")}
                    tooltip={t("point.scheduledReboot.nextSlotTooltip")}
                    value={nextReboot && nextRebootWeekday
                        ? `${t(`weekday.${nextRebootWeekday.key}`)} ${nextReboot.date} ${nextReboot.time}`
                        : t("point.scheduledReboot.none")}
                />
                {lastReboot ? (
                    <>
                        <DetailRow
                            label={t("point.scheduledReboot.lastStatus")}
                            value={statusAndReason(lastReboot)}
                        />
                        <DetailRow
                            label={t("point.scheduledReboot.lastSlot")}
                            value={formatSlot(lastReboot.scheduledSlotUtc)}
                        />
                        <DetailRow
                            label={t("point.scheduledReboot.lastUpdated")}
                            value={formatSlot(lastReboot.updatedDateTimeUtc)}
                        />
                    </>
                ) : (
                    <DetailRow
                        label={t("point.scheduledReboot.lastStatus")}
                        value={t("point.scheduledReboot.none")}
                    />
                )}
            </FormGrid>
        </FormCard>
    );
};

export default ScheduledRebootForm;
